import ThemeToggle from '../shared/ThemeToggle';
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useSocket } from '../../hooks/useSocket';
import { API_BASE_URL } from '../config/api';
import { kitchenSizeName } from '../../utils/sellableSizes';
import {
  ChefHat,
  Check,
  RefreshCw,
  Volume2,
  VolumeX,
  Loader2,
  Timer,
  LogOut,
  Bell,
  Flame
} from 'lucide-react';

interface OrderItem {
  id: number;
  product_name: string;
  size_name: string | null;
  // Απόφαση 15 (BE-16): τα υπολογίζει ο backend ανά είδος. hide_size_name === true → το
  // όνομα του μοναδικού πωλήσιμου μεγέθους δεν δείχνεται· απόν (παλιό backend) = εμφάνιση.
  size_is_single?: boolean;
  hide_size_name?: boolean;
  quantity: number;
  extras: any[];
  removed_ingredients: any[];
  notes: string | null;
  status: string;
  course_number?: number;
}


interface KitchenOrder {
  id: number;
  order_number: number;
  session_id: number;
  table_number: string;
  zone_name: string | null;
  status: string;
  items: OrderItem[];
  notes: string | null;
  created_at: string;
  total_amount: number;
  // Fiscal document status
  fiscal_status?: string;
  fiscal_mark?: string;
  fiscal_retry_count?: number;
  fiscal_failure_message?: string;
}

const KitchenDisplay: React.FC = () => {
  const { t } = useTranslation('kitchen');
  const { store, employeeLogout } = useAuth();
  const { socket, isConnected } = useSocket();
  
  const [orders, setOrders] = useState<KitchenOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [updatingItem, setUpdatingItem] = useState<number | null>(null);
  const [, setTick] = useState(0); // For timer updates
  const [preparingOrders, setPreparingOrders] = useState<Set<number>>(new Set());
  const courseLabels = [
    t('courses.first'), t('courses.second'), t('courses.third'),
    t('courses.fourth'), t('courses.fifth')
  ];

  // Fetch pending kitchen orders
  const fetchOrders = useCallback(async () => {
    if (!store?.id) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/kitchen/orders`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      }
    } catch (error) {
      console.error('Failed to fetch kitchen orders:', error);
    } finally {
      setLoading(false);
    }
  }, [store?.id]);

  // Initial load
  useEffect(() => {
    fetchOrders();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  // Update timer every minute
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  // WebSocket listeners
  useEffect(() => {
    if (!socket) return;
    
    // New order from waiter
    socket.on('kitchen-order', (data: any) => {
      // Play sound
      if (soundEnabled) {
        playNotificationSound();
      }
      
      // Add to orders list with items having 'pending' status
      const itemsWithStatus = (data.items || []).map((item: any) => ({
        ...item,
        status: 'pending'
      }));
      
      // Add new order at the END (oldest orders first = highest priority)
      setOrders(prev => [...prev, {
        id: data.order_id,
        order_number: data.order_number,
        session_id: data.session_id,
        table_number: data.table_number,
        zone_name: data.zone_name,
        status: 'pending',
        items: itemsWithStatus,
        notes: data.notes,
        created_at: new Date().toISOString(),
        total_amount: data.total_amount
      }]);

      // Απόφαση 15: το socket φέρνει τα είδη χωρίς hide_size_name (το υπολογίζει μόνο το
      // GET kitchen/orders, BE-16), άρα το «Κανονικό» ενός μονομεγέθους θα έμενε στην οθόνη
      // ως τον επόμενο 30″ γύρο ενώ το χαρτί της κουζίνας δεν το γράφει. Μία ανάγνωση
      // αμέσως μετά ευθυγραμμίζει την οθόνη· η παραγγελία έχει ήδη γίνει COMMIT πριν το emit.
      fetchOrders();
    });

    return () => {
      socket.off('kitchen-order');
    };
  }, [socket, soundEnabled, fetchOrders]);

  // Play notification sound
  const playNotificationSound = () => {
    try {
      const audio = new Audio('/notification.mp3');
      audio.volume = 0.5;
      audio.play().catch(() => {
      });
    } catch (e) {
    }
  };

  // Mark individual item as ready
  const markItemReady = async (orderId: number, itemId: number) => {
    if (!store?.id) return;
    
    setUpdatingItem(itemId);
    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/order-items/${itemId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'ready' })
      });
      
      if (response.ok) {
        // Update item status locally
        setOrders(prev => prev.map(order => {
          if (order.id !== orderId) return order;
          
          const updatedItems = order.items.map(item => 
            item.id === itemId ? { ...item, status: 'ready' } : item
          );
          
          // Check if all items are ready
          const allReady = updatedItems.every(item => item.status === 'ready');
          
          if (allReady) {
            // Mark entire order as ready
            markOrderReady(orderId);
          }
          
          return { ...order, items: updatedItems };
        }));
      }
    } catch (error) {
      console.error('Failed to mark item ready:', error);
    } finally {
      setUpdatingItem(null);
    }
  };

  // Mark entire order as ready (notifies waiter)
  const markOrderReady = async (orderId: number) => {
    if (!store?.id) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'ready' })
      });
      
      if (response.ok) {
        // Remove from list
        setOrders(prev => prev.filter(o => o.id !== orderId));
      }
    } catch (error) {
      console.error('Failed to mark order ready:', error);
    }
  };

  // Mark order as preparing (intermediate status before ready)
  const markOrderPreparing = async (orderId: number) => {
    if (!store?.id) return;

    // Optimistically update UI
    setPreparingOrders(prev => new Set(prev).add(orderId));

    try {
      await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'preparing' })
      });
    } catch (error) {
      console.error('Failed to mark order preparing:', error);
      // Revert on failure
      setPreparingOrders(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };

  // Call waiter from KDS (sends waiter_call socket event)
  const callWaiter = async (sessionId: number) => {
    if (!store?.id) return;

    try {
      await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/sessions/${sessionId}/call-waiter`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      console.error('Failed to call waiter:', error);
    }
  };

  // Calculate time since order
  const getTimeSince = (dateString: string) => {
    const now = new Date();
    const orderTime = new Date(dateString);
    const diffMs = now.getTime() - orderTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return t('justNow');
    if (diffMins < 60) return `${diffMins}m`;
    
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h${mins}m`;
  };

  // Get time color based on elapsed time
  const getTimeColor = (dateString: string) => {
    const now = new Date();
    const orderTime = new Date(dateString);
    const diffMins = Math.floor((now.getTime() - orderTime.getTime()) / 60000);
    
    if (diffMins < 5) return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/50';
    if (diffMins < 10) return 'text-yellow-700 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/50';
    if (diffMins < 15) return 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/50';
    return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/50 animate-pulse';
  };

  // Parse JSON safely
  const parseJson = (data: any) => {
    if (!data) return [];
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch {
        return [];
      }
    }
    return data;
  };

  // Count ready items
  const getReadyCount = (items: OrderItem[]) => {
    return items.filter(i => i.status === 'ready').length;
  };

  if (loading) {
    return (
      <div className="h-screen bg-surface-0 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="h-screen bg-surface-0 text-t1 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="role-header bg-surface-1 border-b border-line px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ChefHat className="w-8 h-8 text-orange-500 light:text-blue-600" />
            <div>
              <h1 className="text-lg font-bold">{t('kitchenDisplay')}</h1>
              <p className="text-xs text-t3">{store?.store_name}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Connection status */}
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
              isConnected ? 'bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400' : 'bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400'
            }`}>
              <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-sm">{isConnected ? t('connected') : t('disconnected')}</span>
            </div>
            
            <ThemeToggle />
              {/* Sound toggle */}
            <button
              aria-pressed={soundEnabled}
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`role-choice p-2 rounded-lg ${soundEnabled ? 'bg-orange-600 light:text-white' : 'bg-surface-2'}`}
              title={soundEnabled ? t('soundOn') : t('soundOff')}
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
            
            {/* Refresh */}
            <button
              onClick={fetchOrders}
              className="p-2 bg-surface-2 rounded-lg hover:bg-surface-3"
              title={t('refresh')}
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            
            {/* Order count */}
            <div className="role-kitchen-count bg-orange-600 light:text-white px-4 py-2 rounded-lg font-bold">
              {orders.length} {t('ordersCount')}
            </div>
            
            {/* Logout */}
            <button
              onClick={employeeLogout}
              className="p-2 bg-red-600 hover:bg-red-700 light:text-white rounded-lg"
              title={t('logout')}
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>
      
      {/* Orders - Horizontal Scroll */}
      <main className="flex-1 overflow-hidden">
        {orders.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center">
            <ChefHat className="w-24 h-24 text-gray-700 mb-4" />
            <h2 className="text-2xl font-bold text-t4">{t('noPendingOrders')}</h2>
            <p className="text-t4 mt-2">{t('newOrdersWillAppear')}</p>
          </div>
        ) : (
          <div className="h-full flex gap-4 p-4 overflow-x-auto">
            {orders.map(order => {
              const readyCount = getReadyCount(order.items);
              const totalItems = order.items.length;
              const allReady = readyCount === totalItems;
              const isPreparing = preparingOrders.has(order.id);

              return (
                <div data-state={allReady ? 'ready' : isPreparing ? 'preparing' : 'pending'}
                  key={order.id}
                  className={`role-kitchen-ticket flex-shrink-0 w-96 h-full bg-surface-1 rounded-xl border-2 flex flex-col ${
                    allReady ? 'border-green-500' : isPreparing ? 'border-amber-500' : 'border-line'
                  }`}
                >
                  {/* Order Header */}
                  <div className={`role-kitchen-ticket-header px-4 py-3 rounded-t-xl flex-shrink-0 ${
                    isPreparing ? 'bg-amber-100 dark:bg-amber-700' : 'bg-surface-2'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="role-kitchen-number text-3xl font-bold text-orange-500">
                            #{order.order_number}
                          </span>
                          <span className="text-xl font-semibold">
                            {t('table')} {order.table_number}
                          </span>
                          {isPreparing && (
                            <span className="flex items-center gap-1 bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                              <Flame className="w-3 h-3" />
                              {t('cooking')}
                            </span>
                          )}
                        </div>
                        {order.zone_name && (
                          <span className="text-sm text-t3">{order.zone_name}</span>
                        )}
                      </div>
                      <div className={`flex items-center gap-1 px-2 py-1 rounded ${getTimeColor(order.created_at)}`}>
                        <Timer className="w-4 h-4" />
                        <span className="font-mono font-bold text-sm">{getTimeSince(order.created_at)}</span>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 bg-surface-3 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${isPreparing ? 'bg-amber-400' : 'bg-green-500'}`}
                          style={{ width: `${(readyCount / totalItems) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-t3">{readyCount}/{totalItems}</span>
                    </div>
                  </div>
                  
                  {/* Order Items - Scrollable, grouped by course */}
                  <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {/* Group items by course number */}
                    {[1, 2, 3, 4, 5].map(courseNum => {
                      const courseItems = order.items.filter(item => (item.course_number || 1) === courseNum);
                      if (courseItems.length === 0) return null;
                      
                      // Check if all items in this course are ready
                      const allCourseReady = courseItems.every(item => item.status === 'ready');
                      
                      return (
                        <div key={courseNum}>
                          {/* Course Header - only show if there are multiple courses */}
                          {order.items.some(item => (item.course_number || 1) !== 1) && (
                            <div className={`flex items-center gap-2 mb-2 ${courseNum > 1 ? 'mt-3' : ''}`}>
                              <div className={`flex-1 h-px ${allCourseReady ? 'bg-green-600' : 'bg-blue-500'}`} />
                              <span className={`text-sm font-bold px-3 py-1 rounded ${
                                allCourseReady 
                                  ? 'bg-green-600 text-white' 
                                  : 'bg-blue-600 text-white'
                              }`}>
                                {courseLabels[courseNum - 1]}
                              </span>
                              <div className={`flex-1 h-px ${allCourseReady ? 'bg-green-600' : 'bg-blue-500'}`} />
                            </div>
                          )}
                          
                          {/* Course Items */}
                          {courseItems.map((item) => {
                            const isReady = item.status === 'ready';
                            const removed = parseJson(item.removed_ingredients);
                            const extras = parseJson(item.extras);
                            
                            return (
                              <button
                                key={item.id}
                                onClick={() => !isReady && markItemReady(order.id, item.id)}
                                disabled={isReady || updatingItem === item.id}
                                className={`role-kitchen-item w-full text-left rounded-lg p-3 transition-all mb-2 ${
                                  isReady 
                                    ? 'bg-green-50 dark:bg-green-900/30 border-2 border-green-600 opacity-60' 
                                    : 'bg-surface-2 hover:bg-surface-3 border-2 border-transparent cursor-pointer'
                                }`}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className={`role-kitchen-quantity px-3 py-1 rounded font-bold text-lg ${
                                        isReady ? 'bg-green-600' : 'bg-orange-600'
                                      } light:text-white dark:text-t1`}>
                                        {item.quantity}x
                                      </span>
                                      <span className={`font-bold text-xl ${isReady ? 'line-through text-t3' : ''}`}>
                                        {item.product_name}
                                      </span>
                                    </div>
                                    {kitchenSizeName(item) && (
                                      <span className="text-t3 text-base ml-12">{kitchenSizeName(item)}</span>
                                    )}
                                    
                                    {/* Modifications */}
                                    {removed.length > 0 && (
                                      <div className="mt-2 ml-12 text-red-600 dark:text-red-400 text-base font-bold">
                                        ✕ {t('withoutLabel')} {removed.map((i: any) => i.name).join(', ')}
                                      </div>
                                    )}
                                    {extras.length > 0 && (
                                      <div className="mt-1 ml-12 text-green-600 dark:text-green-400 text-base font-bold">
                                        + {t('extraLabel')} {extras.map((i: any) => i.name).join(', ')}
                                      </div>
                                    )}
                                    {item.notes && (
                                      <div className="mt-2 ml-12 text-yellow-700 dark:text-yellow-400 text-base font-medium">
                                        📝 {item.notes}
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Status indicator */}
                                  <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                                    isReady ? 'bg-green-600 light:text-white' : 'bg-surface-3'
                                  }`}>
                                    {updatingItem === item.id ? (
                                      <Loader2 className="w-6 h-6 animate-spin" />
                                    ) : isReady ? (
                                      <Check className="w-8 h-8" />
                                    ) : (
                                      <span className="text-t2 dark:text-t3 text-sm font-bold">{t('tapLabel')}</span>
                                    )}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Order Notes */}
                  {order.notes && (
                    <div className="px-3 pb-2 flex-shrink-0">
                      <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded-lg px-3 py-2">
                        <span className="text-yellow-700 dark:text-yellow-400 text-base font-medium">📝 {order.notes}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Footer Buttons */}
                  <div className="p-3 border-t border-line flex-shrink-0 flex flex-col gap-2">
                    {/* "Σε εξέλιξη" button — only when not yet preparing and not all ready */}
                    {!isPreparing && !allReady && (
                      <button
                        onClick={() => markOrderPreparing(order.id)}
                        className="w-full py-2.5 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-lg flex items-center justify-center gap-2"
                      >
                        <Flame className="w-5 h-5" />
                        <span>{t('inProgressButton')}</span>
                      </button>
                    )}
                    {/* "Κάλεσε Σερβιτόρο" — always available when session exists */}
                    {order.session_id && (
                      <button
                        onClick={() => callWaiter(order.session_id)}
                        className="w-full py-2.5 bg-surface-3 hover:bg-surface-4 text-t1 font-bold rounded-lg flex items-center justify-center gap-2"
                      >
                        <Bell className="w-5 h-5" />
                        <span>{t('callWaiter')}</span>
                      </button>
                    )}
                    {/* "Ειδοποίηση Σερβιτόρου" — only when all items ready */}
                    {allReady && (
                      <button
                        onClick={() => markOrderReady(order.id)}
                        className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg flex items-center justify-center gap-2"
                      >
                        <Check className="w-6 h-6" />
                        <span className="text-lg">{t('notifyWaiterReady')}</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default KitchenDisplay;
